/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    chartZAxisColor,
    chartTableColors
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './commentingbeelinereport_styles';


// component constants
const products = [
    { id: 'commentingBeelineClicks', name: 'Commenting Beeline Clicks', color: chartTableColors[0]},
    { id: 'avgPercentOfVisitors', name: 'Avg Percent of Visitors who Click', color: chartZAxisColor }
];
/* ========== ~~~~~~~~~~ COMMENTING ANALYTICS - COMMENTING BEELINE REPORT ~~~~~~~~~~ ========== */
const CommentingBeelineReport = injectSheet(styles)((props) => {
    
    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.commenting.commentingBeeline);
    const { filters } = useSelector((state) => state.app);
    const changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // show aggregate total on load of page
    useEffect(() => {
        if (status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'commenting');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        setChartLegends(productArr);
    };

    const barChartKeys = useMemo(() => products.map(product => product.name).reverse(), []);

    const barChartColors = useMemo(() => products.map(product => product.color).reverse(), []);

    const barChartLegends = useMemo(() => {
        return chartLegends.map((legend, index) => {
            let value = legend.value;

            if(legend.id === 'avgPercentOfVisitors') 
                value = legend.value < 1 ? `${Number(legend.value).toFixed(2)} %`.replace(/^0./, '.') : `${Number(legend.value).toFixed(1)} %`;
            else 
                value = formatNumber(legend.value);

            
            return (
                <ChartLegend
                    title={legend.name}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        setChartLegends(legends);
                    }}
                    value={value}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'commenting'}
                />
            )
        })
    }, [changeType, chartLegends])

    const barChartData = useMemo(() => {
        const dates = details ? details[selectedInterval] : {};
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if (checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    if (product.id === 'avgPercentOfVisitors')
                        item['z'] = detailsDateData[product.id];
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }, [chartLegends, details, selectedInterval])

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if (productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Commenting Beeline'}
                compareType={changeType}
                metricType={'commenting'}
                percentChange={{
                    value: overview && overview.period ? formatNumber(overview.period.commentingBeelineClicksRateChange, 'percent') : 0,
                    direction: overview && overview.period ? getChangeRateDirection(overview.period.commentingBeelineClicksRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.commentingBeelineClicks) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={barChartColors}
            keys={barChartKeys}
            data={barChartData}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zType={'percentage'}
            zAxisId={'avgPercentOfVisitors'}
            maxZAxisValue={1.5}
            zAxisColor={chartZAxisColor}
        />
    );

  const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'commenting'}
            products={products}
            data={details ? details[selectedInterval] : null}
            csvFileName={'Commenting Beeline Report'}
        />
    ) : []

    const isNoData = status === 'rejected' || 
        (status === 'fulfilled' && !overview?.period?.commentingBeelineClicks) ||
        (status === 'fulfilled' && !Object.keys(overview).length);

    // certain users has access to commenting beeline. Below condition checks if user has access to this module.
    if (!overview?.period?.isCommentingBeelineEnabled) {
        return null;
    }
    return (
        <div name={'commentingbeelinereport'} class={`${props.classes.commentingbeelinereport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Commenting Beeline'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Commenting Beeline</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                Commenting Beeline is the icon that publishers put at the top of the page that gives a count of comments and links directly to the commenting unit. The Avg Percent of Visitors who Click is calculated by taking the total number of clicks of the Commenting Beeline icon divided by the total number of commenting loads during that same period
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                // loading={status === 'pending' ? true : false}
                noData={isNoData}
                enabledPeriods={enabledRollups}
                chartLegend={barChartLegends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommentingBeelineReport;
